import { Button, Menu, MenuItem, Popover, Intent } from "@snpjs/core";
import { Download, FileCsv, MicrosoftExcelLogo } from "@snpjs/icons";
import * as XLSX from "xlsx";
import { useMemo, useState } from "react";

import { AdminViewApi } from "../../../api/AdminViewApi";
import { ILicenseLogEntry } from "../../../shared/model/ILicenseLogEntry";
import { LabeledSelect } from "../../../components/labeledSelect/LabeledSelect";
import { Table2 } from "../../../components/table2/Table2";
// import { Message } from "../../../services/Message";
import { texts } from "../../../i18n/texts";
import { useNavigate } from "react-router-dom";
import { Routes } from "../../../routes/Routes";
import { useRequest } from "../../../hooks/useRequest";
import useTranslation from "../../../hooks/useTranslation";
import styles from "./LicenseLogSection.module.scss";

const YEAR_RANGE = 10;

const buildYearOptions = (): number[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: YEAR_RANGE }, (_, index) => currentYear - index);
};

/**
 * Section for selecting a year and loading license log data.
 */
export const LicenseLogSection: React.FC = () => {
  const { t } = useTranslation();
  const request = useRequest();
  const navigate = useNavigate();
  const yearOptions = useMemo(() => buildYearOptions(), []);
  const [selectedYear, setSelectedYear] = useState<number>(yearOptions[0]);
  const [licenseLogData, setLicenseLogData] = useState<ILicenseLogEntry[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  // const [reportUrl, setReportUrl] = useState<string>();
  const [visibleCount, setVisibleCount] = useState<number>(0);

  const onConfirm = async () => {
    await fetchForYear(selectedYear, true);
    navigate(`${Routes.adminView.toPath()}/license-log`);
  };

  const filteredData = useMemo(() => {
    return licenseLogData.filter((entry) => {
      const raw =
        (entry as any)["CREATION_DATE"] ||
        (entry as any)["creation_date"] ||
        (entry as any)["CreationDate"];

      if (!raw) return false;
      const date = new Date(String(raw));
      return !isNaN(date.getTime()) && date.getFullYear() === selectedYear;
    });
  }, [licenseLogData, selectedYear]);

  const exportData = (format: "csv" | "excel", rows: ILicenseLogEntry[] = filteredData) => {
    if (rows.length === 0) return;

    const columns = Object.keys(rows[0] ?? {});
    const escapeCell = (value: unknown): string => {
      const text = value == null ? "" : String(value).replace(/\r?\n/g, " ");
      return `"${text.replace(/"/g, '""')}"`;
    };

    if (format === "csv") {
      const csv = [
        columns.join(","),
        ...rows.map((row) => columns.map((column) => escapeCell((row as Record<string, unknown>)[column])).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `license_log_${selectedYear}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const workbook = XLSX.utils.book_new();
    const sheetData = rows.map((row) => {
      const values: Record<string, unknown> = {};
      columns.forEach((column) => {
        values[column] = (row as Record<string, unknown>)[column] ?? "";
      });
      return values;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetData, { header: columns });
    XLSX.utils.book_append_sheet(workbook, worksheet, "LicenseLog");

    const workbookBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([workbookBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `license_log_${selectedYear}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportMenu = (table?: any) => (
    <Menu>
      <MenuItem
        icon={<FileCsv />}
        text="CSV"
        onClick={() => exportData("csv", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : filteredData)}
      />
      <MenuItem
        icon={<MicrosoftExcelLogo />}
        text="Excel"
        onClick={() => exportData("excel", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : filteredData)}
      />
    </Menu>
  );

  const fetchForYear = async (year: number, markLoaded = false) => {
    setIsLoading(true);

    await request.send(async () => {
      const data = await AdminViewApi.getLicenseLog(year);
      setLicenseLogData(data);
      if (markLoaded) setHasLoaded(true);
      else setHasLoaded(true);
    });

    setIsLoading(false);
  };

  // const onShowReport = async () => {
  //   if (filteredData.length === 0) {
  //     Message.warning(t(texts.adminView.noLicenseLogData));
  //     return;
  //   }

  //   setIsSubmittingReport(true);
  //   setReportUrl(undefined);

  //   const url = await request.send(async () => {
  //     await AdminViewApi.bulkInsertLicenseLogReport(filteredData);
  //     return AdminViewApi.getLicenseLogReportUrl();
  //   });

  //   if (url) {
  //     setReportUrl(url);
  //     Message.info(t(texts.adminView.showReportSuccess));
  //   }

  //   setIsSubmittingReport(false);
  // };

  return (
    <div className={styles.wrapper}>
      <div className={styles.controls}>
        <LabeledSelect
          className={styles.yearSelect}
          items={yearOptions}
          label={t(texts.adminView.year)}
          onSelect={(year) => {
            if (year === undefined) return;
            setSelectedYear(year);
            void fetchForYear(year);
          }}
          selected={selectedYear}
          renderOptionTitle={(year) => year.toString()}
        />
        <Button
          className={styles.confirmButton}
          intent="primary"
          text={t(texts.adminView.confirm)}
          onClick={onConfirm}
          loading={isLoading}
        />
        {hasLoaded && (
          <>
            <div className={styles.countHeader}>{visibleCount} Records Found</div>
            {/* <Button
              className={styles.showReportButton}
              intent="primary"
              text={t(texts.adminView.showReport)}
              onClick={onShowReport}
              loading={isSubmittingReport}
              disabled={filteredData.length === 0 || isLoading}
            /> */}
          </>
        )}
      </div>

      {hasLoaded && (
        <div className={styles.tableContainer}>
          <Table2
            data={filteredData}
            isLoading={isLoading}
            noResultsTitle={t(texts.adminView.noLicenseLogData)}
            onVisibleCountChange={(count) => setVisibleCount(count)}
            showUtilityToolbar={false}
            enableGlobalFilter={false}
            leftToolbar={(table) => (
              <Popover content={exportMenu(table)} placement="bottom-start" usePortal={false}>
                <Button
                  intent={Intent.PRIMARY}
                  icon={<Download />}
                  rightIcon="caret-down"
                  text="Export"
                  fill
                  alignText="left"
                />
              </Popover>
            )}
          />
        </div>
      )}
      {/* {reportUrl && (
        <div className={styles.reportContainer}>
          <iframe className={styles.reportFrame} src={reportUrl} title="Licence Log report" />
        </div>
      )} */}
    </div>
  );
};
